import React from 'react';
import { Helmet } from 'react-helmet-async';

const udemyLink = 'https://www.udemy.com/course/ai-agents-for-absolute-beginners/?referralCode=220DD2B1D8A86CBF4F51';

const frameworks = [
  {
    title: 'OpenAI Agents SDK',
    description: 'Build AI agents from scratch in Python with tool calling, memory, web search, and multi-turn conversations using the OpenAI Agents SDK.',
    icon: '🤖',
    color: 'from-orange-500 to-amber-500'
  },
  {
    title: 'Google ADK (Agent Development Kit)',
    description: 'Build agents, add tools, and create multi-agent applications with Google\'s open-source Agent Development Kit.',
    icon: '🧩',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    title: 'Multi-Agent Systems',
    description: 'Design multi-agent architectures where specialized AI agents collaborate, delegate, and solve complex tasks together.',
    icon: '🕸️',
    color: 'from-purple-500 to-pink-500'
  },
  {
    title: 'FastAPI Deployment',
    description: 'Deploy your AI agents as real, production-ready APIs using FastAPI.',
    icon: '🚀',
    color: 'from-emerald-500 to-teal-500'
  },
  {
    title: 'Real-World AI Projects',
    description: 'Build practical AI applications including autonomous assistants, stock monitoring agents, and collaborative multi-agent workflows.',
    icon: '💹',
    color: 'from-rose-500 to-orange-500'
  }
];

const topics = [
  { label: 'AI Agents', icon: '🤖' },
  { label: 'Agentic AI', icon: '⚙️' },
  { label: 'Tool Calling', icon: '🛠️' },
  { label: 'Multi-Agent Systems', icon: '🕸️' },
  { label: 'Agent Architecture', icon: '🏗️' },
  { label: 'Agent Design Patterns', icon: '📐' },
  { label: 'OpenAI Agents SDK', icon: '🤝' },
  { label: 'Google ADK', icon: '🔵' }
];

const BuildAgents: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>AI Agents for Absolute Beginners - Complete Course (2026) | FutureXSkills</title>
        <meta
          name="description"
          content="Learn to build AI agents from scratch with Python, the OpenAI Agents SDK, and Google ADK. A hands-on course covering tool calling, memory, web search, multi-agent systems, and FastAPI deployment."
        />
        <meta
          name="keywords"
          content="AI Agents for Absolute Beginners, OpenAI Agents SDK, Google ADK, Agent Development Kit, AI Agent, AI Agents, Multi Agent, Multi Agent System, Agentic AI, Tool Calling, Memory, Web Search, FastAPI, AI Engineering, AI Agent Architecture, AI Agent Design Patterns, AI Tutorial, AI Development, Python, AI Course, Artificial Intelligence, FutureXSkill"
        />
        <link rel="canonical" href="https://futurexskills.com/build-agents" />

        {/* Open Graph */}
        <meta property="og:title" content="AI Agents for Absolute Beginners - Complete Course (2026)" />
        <meta property="og:description" content="Build AI agents from scratch with Python, the OpenAI Agents SDK, and Google ADK. Tool calling, memory, web search, multi-agent systems, and FastAPI deployment." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://futurexskills.com/build-agents" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI Agents for Absolute Beginners - Complete Course (2026)" />
        <meta name="twitter:description" content="Build AI agents from scratch with Python, the OpenAI Agents SDK, and Google ADK." />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-gray-900">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 via-cyan-900/30 to-teal-900/30" />
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl animate-pulse-slow" />
            <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }} />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 px-4 py-2 rounded-full mb-6 border border-emerald-500/30">
              <span className="text-emerald-400 font-semibold">🤖 Agentic AI Development</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
              <span className="gradient-text">Build AI Agents from Scratch</span>
            </h1>
            <h2 className="text-xl md:text-2xl font-semibold text-gray-200 mb-8 max-w-3xl mx-auto">
              Complete Course
            </h2>

            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
              Go beyond simple chatbots. Learn to build production-ready AI agents with tool calling, memory, web search,
              FastAPI deployment, and multi-agent systems using the OpenAI Agents SDK and Google ADK.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={udemyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full sm:w-auto"
              >
                🎓 View Course on Udemy
              </a>
            </div>

            <p className="text-gray-400 text-sm mt-6">
              Hands-on, project-based learning with source code included.
            </p>
          </div>
        </section>

        {/* Social Proof Section */}
        <section className="py-8 bg-gray-800 border-y border-white/10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-gray-200">
              <span className="font-semibold">⭐ 100,000+ Learners</span>
              <span className="hidden sm:inline text-gray-600">•</span>
              <span>Complete Course</span>
              <span className="hidden sm:inline text-gray-600">•</span>
              <span>Hands-on Projects</span>
              <span className="hidden sm:inline text-gray-600">•</span>
              <span>Beginner Friendly</span>
            </div>
          </div>
        </section>

        {/* Frameworks Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
              What You'll Build
            </h2>
            <p className="text-center text-gray-200 mb-12 max-w-3xl mx-auto text-lg">
              Hands-on projects with the frameworks and tools powering modern agentic AI development.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {frameworks.map((framework) => (
                <div key={framework.title} className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all">
                  <div className={`w-14 h-14 bg-gradient-to-r ${framework.color} rounded-lg flex items-center justify-center mb-4`}>
                    <span className="text-2xl">{framework.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{framework.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{framework.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Topics Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-4">
              Skills You'll Learn
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto text-lg">
              From your first AI agent to production-ready multi-agent systems and deployment.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {topics.map((topic) => (
                <div key={topic.label} className="glass-card p-5 text-center hover:bg-white/10 transition-all">
                  <div className="text-3xl mb-2">{topic.icon}</div>
                  <div className="text-gray-800 font-semibold">{topic.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-3xl p-12 border border-white/10">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Start Building AI Agents Today
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                Enroll in the course and start building real AI agents with Python.
              </p>
              <a
                href={udemyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold px-10 py-5 rounded-xl transition-all transform hover:scale-105 shadow-xl text-xl"
              >
                🎓 View Course on Udemy
                <span className="ml-3 text-2xl">→</span>
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BuildAgents;
